import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import posthog from "posthog-js";
import { isPostHogEnabled } from "@/lib/posthog";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured, setCachedSession, resolveInitialAuthAndCache, isInvalidRefreshTokenError, redirectToLoginOnSessionExpired, readPersistedSessionFallback } from "@/lib/supabase";
import { isPasswordRecoveryUrl } from "@/lib/authRecovery";
import { isPublicPath } from "@/lib/publicRoutes";
import { queryClient } from "@/lib/queryClient";
import { pinService } from "@/services/pinService";
import { registerSessionExpiredHandler, notifySessionRestored } from "@/lib/sessionExpiry";
import { purgeThreadCaches } from "@/lib/sidebarRecencyCache";

const AUTH_USER_QUERY_KEY = ["auth-user"] as const;
const AUTH_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes - single source, no refetch on focus
const VISIBILITY_RECHECK_THROTTLE_MS = 5 * 60 * 1000; // Only re-validate with getUser() at most every 5 min

function identifyUserForPostHog(user: User) {
  if (isPostHogEnabled()) {
    posthog.identify(user.id);
    posthog.setPersonProperties({
      email: user.email,
      username: user.username,
    });
  }
}

function resetPostHog() {
  if (isPostHogEnabled()) {
    posthog.reset();
  }
}

interface User {
  id: string;
  username: string;
  email?: string;
  createdAt?: string; // ISO date string from Supabase
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  /** True when the session died silently (expired/revoked refresh token) while the user was
   * already signed in. `user` is deliberately left populated so the current page stays mounted;
   * consumers should show an in-app "please sign in again" prompt rather than redirecting. */
  sessionExpired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function toUser(authUser: any): User {
  return {
    id: authUser.id,
    username: authUser.email || authUser.user_metadata?.username || authUser.id,
    email: authUser.email,
    createdAt: authUser.created_at,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const lastVisibilityGetUserRef = useRef<number>(0);
  const isExplicitLogoutRef = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    return registerSessionExpiredHandler(() => setSessionExpired(true));
  }, []);

  const {
    data: user = null,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: AUTH_USER_QUERY_KEY,
    queryFn: async (): Promise<User | null> => {
      if (!isSupabaseConfigured) {
        resolveInitialAuthAndCache(null);
        return null;
      }
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);
      const verificationType = hashParams.get("type") || queryParams.get("type");
      const isSignupVerification = verificationType === "signup";

      if (isPasswordRecoveryUrl()) {
        resolveInitialAuthAndCache(null);
        return null;
      }
      if (isSignupVerification) {
        window.history.replaceState(null, "", window.location.pathname);
        await supabase.auth.signOut();
        resolveInitialAuthAndCache(null);
        const loginUrl = new URL("/login", window.location.origin);
        loginUrl.searchParams.set("verified", "true");
        window.location.href = loginUrl.toString();
        return null;
      }
      try {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Session check timeout")), 15000)
        );
        const { data: { session } } = (await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise,
        ])) as { data: { session: any } };
        resolveInitialAuthAndCache(session);
        return session?.user ? toUser(session.user) : null;
      } catch (error) {
        console.error("Error checking session:", error);
        if (isInvalidRefreshTokenError(error)) {
          resolveInitialAuthAndCache(null);
          resetPostHog();
          // Clears local session and redirects with returnTo preserved.
          await redirectToLoginOnSessionExpired();
          return null;
        }
        // Timeout or other transient failure: trust the locally persisted session instead of
        // treating the user as logged out (which unmounts the app and dumps them to /login).
        // onAuthStateChange corrects the cache once the real session settles. Also unblocks
        // getCurrentSession() waiters on initialAuthPromise.
        const persisted = readPersistedSessionFallback();
        resolveInitialAuthAndCache(persisted);
        return persisted?.user ? toUser(persisted.user) : null;
      }
    },
    staleTime: AUTH_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!isSuccess || !user) return;
    identifyUserForPostHog(user);
  }, [isSuccess, user?.id]);

  /** Welcome credits + pending signup coupon (localStorage) after session exists */
  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured) return;
    let cancelled = false;
    (async () => {
      try {
        const { creditsService } = await import("@/services/creditsService");
        const boot = await creditsService.bootstrapRegistrationCredits(queryClient);
        if (cancelled) return;
        if (boot.couponApplied && boot.bonusCredits != null) {
          toast({
            title: "Signup coupon applied",
            description: `+${boot.bonusCredits.toLocaleString()} bonus credits added to your account.`,
          });
        }
      } catch (e) {
        console.warn("bootstrapRegistrationCredits:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- toast; avoid re-running bootstrap

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const isSignupVerification =
          hashParams.get("type") === "signup" || new URLSearchParams(window.location.search).get("type") === "signup";

        if (event === "PASSWORD_RECOVERY" || isPasswordRecoveryUrl()) {
          setCachedSession(null);
          queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
          return;
        }
        if (isSignupVerification && session?.user) {
          window.history.replaceState(null, "", window.location.pathname);
          await supabase.auth.signOut();
          setCachedSession(null);
          queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
          const loginUrl = new URL("/login", window.location.origin);
          loginUrl.searchParams.set("verified", "true");
          window.location.href = loginUrl.toString();
          return;
        }
        setCachedSession(session);

        if (session?.user) {
          // Valid session (fresh sign-in, or a silent refresh that succeeded): clear any
          // soft-expiry prompt and proceed as normal.
          setSessionExpired(false);
          const mappedUser = toUser(session.user);
          queryClient.setQueryData(AUTH_USER_QUERY_KEY, mappedUser);
          identifyUserForPostHog(mappedUser);
          return;
        }

        if (isExplicitLogoutRef.current) {
          // Real logout initiated by the user: clear everything as before.
          isExplicitLogoutRef.current = false;
          queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
          // DS EDIT 6: thread-list caches hold sensitive titles — purge in
          // every tab so the next account never sees them.
          purgeThreadCaches();
          resetPostHog();
          return;
        }

        if (queryClient.getQueryData(AUTH_USER_QUERY_KEY)) {
          // Session died unexpectedly (revoked/expired refresh token) while the user was already
          // signed in. Do NOT null the user query — that would unmount the whole authenticated
          // tree (App.tsx gates on `!user`) and destroy any in-progress chat/streaming state.
          // Show the in-app soft-expiry prompt instead and keep the page mounted.
          setSessionExpired(true);
          return;
        }

        // No prior user and no session: genuinely signed out already, nothing to unmount.
        queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (isPublicPath(window.location.pathname)) return;
      const now = Date.now();
      if (now - lastVisibilityGetUserRef.current < VISIBILITY_RECHECK_THROTTLE_MS)
        return;
      lastVisibilityGetUserRef.current = now;
      supabase.auth
        .getUser()
        // supabase-js returns errors in the result object (it does not throw), so the
        // error must be inspected here: only a DEFINITIVE auth failure may sign the user
        // out. Treating any missing user as signed-out turned every network blip on tab
        // focus into a dump-to-login.
        .then(async ({ data, error }) => {
          if (data?.user) return;
          if (error && isInvalidRefreshTokenError(error)) {
            // Refresh token is dead (e.g. user was away long enough for it to expire/rotate
            // elsewhere). Keep `user` populated and the page mounted; surface the soft in-app
            // "please sign in again" prompt instead of nulling state and hard-redirecting —
            // that used to blow away any in-progress chat draft/streaming state on tab focus.
            setCachedSession(null);
            setSessionExpired(true);
            return;
          }
          const sessionMissing =
            !error || (error as { name?: string }).name === "AuthSessionMissingError";
          if (sessionMissing) {
            // Genuinely no session (e.g. signed out in another tab): clear local state.
            setCachedSession(null);
            queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
            resetPostHog();
          }
          // Anything else (network failure, 5xx, throttled refresh) is transient:
          // keep the session and let the next visibility check retry.
        })
        .catch(() => {
          // Fetch-level rejection is transient by definition — never sign out on it.
        });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const login = async (username: string, password: string) => {
    if (!isSupabaseConfigured) {
      toast({
        title: "Login unavailable",
        description: "Authentication service is not configured. Please try again later.",
        variant: "destructive",
      });
      throw new Error("Authentication service is not configured");
    }
    
    try {
      const identifier = username.trim();
      let email = identifier;

      // Username login: resolve email from public.users (Auth only accepts email)
      if (identifier && !identifier.includes("@")) {
        const normalizedUsername = identifier.toLowerCase();
        const { data: profile, error: lookupError } = await supabase
          .from("users")
          .select("email")
          .eq("username", normalizedUsername)
          .maybeSingle();

        if (lookupError && lookupError.code !== "PGRST116") {
          console.warn("username login lookup:", lookupError);
        }

        if (!profile?.email) {
          throw new Error("Invalid username/email or password");
        }
        email = profile.email;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        let errorMessage = 'Login failed';
        
        // Handle specific Supabase auth errors
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid username/email or password';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please verify your email before signing in';
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Too many login attempts. Please try again later';
        } else {
          errorMessage = error.message || 'Login failed';
        }
        
        throw new Error(errorMessage);
      }

      if (data.user && data.session) {
        const userData = toUser(data.user);
        setCachedSession(data.session);
        queryClient.setQueryData(AUTH_USER_QUERY_KEY, userData);
        identifyUserForPostHog(userData);
        setSessionExpired(false);
        notifySessionRestored();
        toast({
          title: "Welcome back!",
          description: `Logged in as ${userData.username}`,
        });
      }
    } catch (error) {
      // 522 / "Failed to fetch" = auth server unreachable (e.g. Supabase down or paused)
      const isNetworkError =
        error instanceof TypeError &&
        (error.message === "Failed to fetch" || (error as Error).message?.includes("fetch"));
      const description = isNetworkError
        ? "The auth server isn’t responding (often due to Supabase issues). Check status.supabase.com and try again later."
        : error instanceof Error
          ? error.message
          : "Invalid credentials";
      toast({
        title: "Login failed",
        description,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signup = async (username: string, password: string) => {
    if (!isSupabaseConfigured) {
      toast({
        title: "Signup unavailable",
        description: "Authentication service is not configured. Please try again later.",
        variant: "destructive",
      });
      throw new Error("Authentication service is not configured");
    }
    
    try {
      // IMPORTANT: The redirect URL must be whitelisted in Supabase Dashboard
      // Go to: Authentication > URL Configuration > Redirect URLs
      // Add: http://localhost:5173 (for dev)
      // Add: https://yourdomain.com (for production)
      const redirectUrl = `${window.location.origin}`;
      console.log('Signup: Using redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.signUp({
        email: username,
        password: password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: username,
          },
        },
      });

      if (error) {
        let errorMessage = 'Signup failed';
        
        // Handle specific Supabase auth errors
        if (error.message.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address';
        } else if (error.message.includes('Email rate limit exceeded')) {
          errorMessage = 'Too many signup attempts. Please try again later';
        } else {
          errorMessage = error.message || 'Could not create account';
        }
        
        throw new Error(errorMessage);
      }

      if (data.user) {
        // D-7: rely on Supabase project-level auto-confirm instead of a client-known confirm
        // secret. When auto-confirm is enabled, signUp() returns a session immediately and the
        // user is signed in with no insecure endpoint. When it is disabled, no session is
        // returned and we fall through to the email-verification message.
        if (data.session) {
          const userData = toUser(data.user);
          setCachedSession(data.session);
          queryClient.setQueryData(AUTH_USER_QUERY_KEY, userData);
          identifyUserForPostHog(userData);
          try {
            const { creditsService } = await import("@/services/creditsService");
            const boot = await creditsService.bootstrapRegistrationCredits(queryClient);
            if (boot.couponApplied && boot.bonusCredits != null) {
              toast({
                title: "Signup coupon applied",
                description: `+${boot.bonusCredits.toLocaleString()} bonus credits added to your account.`,
              });
            }
          } catch (e) {
            console.warn("bootstrapRegistrationCredits after signup:", e);
          }
          toast({
            title: "Account created!",
            description: "You're signed in. Welcome!",
          });
          return;
        }
        toast({
          title: "Account created!",
          description: "Please check your email to verify your account before signing in.",
        });
      }
    } catch (error) {
      toast({
        title: "Signup failed",
        description: error instanceof Error ? error.message : "Could not create account",
        variant: "destructive",
      });
      throw error;
    }
  };

  const logout = async () => {
    if (!isSupabaseConfigured) {
      pinService.invalidateSession();
      queryClient.invalidateQueries({ queryKey: ["pin-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
      setCachedSession(null);
      queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
      setSessionExpired(false);
      purgeThreadCaches();
      resetPostHog();
      return;
    }

    // Tells the onAuthStateChange listener this SIGNED_OUT is intentional, not a dead refresh
    // token, so it clears `user` instead of routing through the soft-expiry prompt.
    isExplicitLogoutRef.current = true;

    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('Logout error:', error);
        throw new Error('Logout failed');
      }

      pinService.invalidateSession();
      queryClient.invalidateQueries({ queryKey: ["pin-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
      setCachedSession(null);
      queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
      setSessionExpired(false);
      resetPostHog();
      toast({
        title: "Logged out",
        description: "You have been logged out successfully",
      });
    } catch (error) {
      console.error('Logout error:', error);
      isExplicitLogoutRef.current = false;
      pinService.invalidateSession();
      queryClient.invalidateQueries({ queryKey: ["pin-status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
      setCachedSession(null);
      queryClient.setQueryData(AUTH_USER_QUERY_KEY, null);
      setSessionExpired(false);
      purgeThreadCaches();
      resetPostHog();
      toast({
        title: "Logged out locally",
        description: "Session cleared on your device",
        variant: "default",
      });
    }
  };

  const resetPassword = async (email: string) => {
    if (!isSupabaseConfigured) {
      toast({
        title: "Password reset unavailable",
        description: "Authentication service is not configured. Please try again later.",
        variant: "destructive",
      });
      throw new Error("Authentication service is not configured");
    }
    
    try {
      // IMPORTANT: The redirect URL must be whitelisted in Supabase Dashboard
      // Go to: Authentication > URL Configuration > Redirect URLs
      // Add: http://localhost:5173/reset-password (for dev)
      // Add: https://yourdomain.com/reset-password (for production)
      const redirectUrl = `${window.location.origin}/reset-password`;
      console.log('Password Reset: Using redirect URL:', redirectUrl);

        const { data, error } = await supabase.auth.resetPasswordForEmail(
            email,
            { redirectTo: redirectUrl }
        )


        console.log('resetPasswordForEmail data:', data);

      /*const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });*/

      if (error) {
        let errorMessage = 'Password reset failed';
        
        // Handle specific Supabase auth errors
        if (error.message.includes('User not found')) {
          errorMessage = 'No account found with this email address';
        } else if (error.message.includes('Email rate limit exceeded')) {
          errorMessage = 'Too many password reset attempts. Please try again later';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address';
        } else {
          errorMessage = error.message || 'Could not send password reset email';
        }
        
        throw new Error(errorMessage);
      }

      toast({
        title: "Password reset email sent",
        description: "Check your email for instructions to reset your password",
      });
    } catch (error) {
      toast({
        title: "Password reset failed",
        description: error instanceof Error ? error.message : "Could not send password reset email",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, resetPassword, isLoading, sessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
